import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus, Search, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * EntityCombobox — LAW-001 canonical picker.
 *
 * Search-as-you-type combobox bound to a Cruise Command reference-library
 * endpoint. Stores the entity's stable id (not free text) so downstream
 * consumers can resolve line/ship/venue records without normalising a
 * user's typing.
 *
 * When no match exists AND `onCreate` is provided, the empty state
 * surfaces a "Create <Entity>" action that immediately provisions the
 * entity via the same picker.
 *
 * Never accepts arbitrary text as a value.
 */
export default function EntityCombobox({
  value,                 // selected entity id (or null)
  valueLabel,            // resolved display name for the selected id (or null)
  items,                 // [{ id, name, sublabel? }]
  loading = false,
  disabled = false,
  placeholder = "Search…",
  emptyLabel = "No matches.",
  createLabel,           // e.g. "Create new cruise line"; omit to disable create-new
  onCreate,              // async (name) => { id, name }
  onChange,              // (id, entity) => void
  testId,
  testIdOption,
  testIdCreate,
  autoFocus = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = `${it.name || ""} ${it.sublabel || ""} ${(it.aliases || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  const exactMatch = useMemo(() => {
    const key = (v) => String(v || "").toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]/g, "");
    const q = key(query);
    if (!q) return true; // don't offer create when query is empty
    return items.some((it) => key(it.name) === q || (it.aliases || []).some((a) => key(a) === q));
  }, [items, query]);

  // P2-CRUISE-LINE-DEDUPE · only offer "Create new" when nothing in the
  // library matches, so an existing entry is never shadowed by accident.
  const canCreate = !!createLabel && !!onCreate && query.trim().length >= 2
    && !exactMatch && filtered.length === 0;

  const handleCreate = async () => {
    if (!onCreate) return;
    setCreating(true);
    try {
      const created = await onCreate(query.trim());
      if (created && created.id) {
        onChange?.(created.id, created);
        setQuery("");
        setOpen(false);
      }
    } finally {
      setCreating(false);
    }
  };

  const selectionText = value && valueLabel ? valueLabel : "";

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        disabled={disabled}
        className={cn(
          "w-full flex items-center justify-between text-left text-[16px] px-4 py-3 border rounded-lg transition-colors",
          "focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100",
          selectionText ? "border-slate-200 text-slate-900" : "border-slate-200 text-slate-400",
          disabled && "opacity-50 cursor-not-allowed bg-slate-50"
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className="truncate">
          {selectionText || placeholder}
        </span>
        <ChevronsUpDown className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…"
              className="flex-1 text-[14px] bg-transparent focus:outline-none placeholder:text-slate-400"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>

          <div className="max-h-96 overflow-y-auto py-1" role="listbox">
            {loading && (
              <div className="px-3 py-4 text-[13px] text-slate-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            )}

            {!loading && filtered.length === 0 && !canCreate && (
              <div className="px-3 py-4 text-[13px] text-slate-500">{emptyLabel}</div>
            )}

            {!loading && filtered.map((it) => {
              const selected = it.id === value;
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => {
                    onChange?.(it.id, it);
                    setQuery("");
                    setOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-start gap-2 px-3 py-2 text-left text-[14px] hover:bg-amber-50",
                    selected && "bg-amber-50/50"
                  )}
                  role="option"
                  aria-selected={selected}
                  data-testid={testIdOption ? `${testIdOption}-${it.id}` : undefined}
                >
                  <Check className={cn("w-4 h-4 mt-0.5 flex-shrink-0", selected ? "text-amber-600" : "text-transparent")} />
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{it.name}</div>
                    {it.sublabel && (
                      <div className="text-[12px] text-slate-500 truncate">{it.sublabel}</div>
                    )}
                  </div>
                </button>
              );
            })}

            {!loading && canCreate && (
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-[14px] border-t border-slate-100 bg-emerald-50/50 hover:bg-emerald-50 text-emerald-800 font-semibold disabled:opacity-60"
                data-testid={testIdCreate}
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                <span className="truncate">
                  {creating ? "Creating…" : `${createLabel}: "${query.trim()}"`}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
